import { useMemo, useState, useEffect } from "react";
import type { ServiceAlert, TripInstance, TripInstanceLiveProgress, Stop } from "../api/types";
import { severityToUi, hasFreshFix, VEHICLE_LIVE_STATE_META } from "../api/types";
import { api } from "../api/client";
import { describeNextStop, nextLiveStop } from "../api/liveProgress";
import { matchNearestSegment, describeNearestSegment, type StopCoord } from "../api/routeMatch";
import { useReferenceData } from "../hooks/useReferenceData";
import { useNowTick } from "../hooks/useNowTick";
import type { AckMap } from "../hooks/useIncidentAcks";
import { rowLabel, rowSubtitle, stateExplanation, vehicleStatusLabel, type LiveVehicleRow, type RouteOption } from "./liveFleet";
import { markerLookMeta } from "./markerLook";

interface MapSidebarProps {
  /** The vehicle list: Layout's live rows after the route filter and the
   *  search, already sorted. */
  rows: LiveVehicleRow[];
  /** The selected bus, looked up across every row, so typing a search after
   *  selecting one does not close its panel. */
  selectedRow?: LiveVehicleRow;
  /** False until the roster, the first fleet snapshot, device sessions and
   *  trips have all arrived. States resolved before then are artefacts of
   *  missing inputs (every bus "No GPS" for want of positions), so the list
   *  waits rather than show them. */
  liveKnown: boolean;
  /** False until the roster has actually loaded. An empty list then means
   *  "not yet", not "this agency owns no buses". */
  rosterLoaded?: boolean;
  /** Buses with a fix from the last 90 seconds. */
  liveCount: number;
  routeFilter: RouteOption | null;
  /** Rows on the chosen route, and in all, for "3 of 35". */
  routeCount: number;
  totalCount: number;
  onClearRouteFilter: () => void;
  /** The search, shared with the top bar's box. */
  query: string;
  onQueryChange: (query: string) => void;
  alerts: ServiceAlert[];
  /** Lifted to Layout.tsx so the same ack state the panic alarm watches is
   *  the one this tab's Acknowledge button writes to — two independent
   *  useIncidentAcks() instances wouldn't see each other's writes within
   *  the same tab (the hook only syncs across tabs, via storage events). */
  acks: AckMap;
  onAck: (incidentId: string) => void;
  selectedVehicleId?: string;
  onSelectVehicle: (id: string | undefined) => void;
  /** Vehicles with an active panic alarm: drawn as on the map, listed first. */
  panicVehicleIds?: ReadonlySet<string>;
  onResolve: (alertId: string) => Promise<void> | void;
}

function fmtDelayShort(seconds?: number | null): string {
  if (seconds == null) return "on time";
  const abs = Math.abs(seconds);
  if (abs < 60) return "on time";
  const sign = seconds >= 0 ? "+" : "−";
  return `${sign}${Math.round(abs / 60)}m`;
}

function delayTone(seconds?: number | null): string {
  if (seconds == null || Math.abs(seconds) < 60) return "ok";
  if (seconds > 300) return "crit";
  if (seconds > 60) return "warn";
  return "early";
}

function fmtAgo(ms: number): string {
  const m = Math.max(0, Math.floor(ms / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
}

// "Orange Line" -> "OL", "N-5" -> "N-5" (short codes with no space pass
// through as-is). Generic — no per-route hardcoding.
function routeBadgeLabel(trip?: TripInstance): string {
  if (!trip) return "";
  const name = (trip.route_short_name || "").trim();
  if (!name) return "";
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 3).toUpperCase();
}

function routeBadgeColor(trip?: TripInstance): string {
  if (!trip?.route_color) return "var(--text-4)";
  return `#${trip.route_color}`;
}

/** Why a row is listed when it is not simply an active bus on the roster. */
function rowChip(row: LiveVehicleRow): string | null {
  if (row.inactiveStatus) return vehicleStatusLabel(row.inactiveStatus);
  if (row.kind === "tablet") return "No bus assigned";
  if (row.kind === "unlisted") return "Not on roster";
  return null;
}

function Glyph({ path, size }: { path: string; size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="#fff" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

// ─── Vehicle Detail Panel ────────────────────────────────────────────────────

interface VehicleDetailProps {
  row: LiveVehicleRow;
  /** The bus has an active panic alarm. */
  panic: boolean;
  now: number;
  onClose: () => void;
}

function VehicleDetailPanel({ row, panic, now, onClose }: VehicleDetailProps) {
  const { position, state, trip, driver } = row;
  interface StopTimeEntry {
    stop_id: string;
    stop_sequence: number;
    arrival_time?: string;
    departure_time?: string;
    actual_arrival?: string;
    actual_departure?: string;
    stop_name?: string;
  }
  const [stopTimes, setStopTimes] = useState<StopTimeEntry[]>([]);
  const [stopIndex, setStopIndex] = useState<Map<string, Stop>>(new Map());

  // The timetable trip, for the static timeline when live progress has
  // nothing: the trip instance names it — tablet fixes carry no trip_id.
  const templateTripId = trip?.trip_id || position?.trip_id;
  useEffect(() => {
    if (!templateTripId) { setStopTimes([]); return; }
    let cancelled = false;
    api.getTripStopTimes(templateTripId).then((st) => {
      if (!cancelled) setStopTimes(Array.isArray(st) ? st : []);
    }).catch(() => setStopTimes([]));
    return () => { cancelled = true; };
  }, [templateTripId]);

  const movingUntracked = state === "moving_no_trip";

  // For an untracked-but-moving bus, to match its raw fix onto the nearest
  // route segment. (A trip's stop times arrive with their stop names.)
  const stopsRef = useReferenceData(() => api.listStops(), {
    label: "stop index",
    enabled: movingUntracked,
  });
  useEffect(() => {
    const resp = stopsRef.data;
    if (!resp) return;
    const list = Array.isArray(resp) ? resp : resp.data ?? [];
    const m = new Map<string, Stop>();
    for (const s of list) m.set(s.id, s);
    setStopIndex(m);
  }, [stopsRef.data]);

  const stopTemplatesRef = useReferenceData(() => api.listStopTemplates(), {
    label: "stop templates",
    enabled: movingUntracked,
  });
  const nearestSegment = useMemo(() => {
    if (!movingUntracked || !position || stopIndex.size === 0) return undefined;
    const templates = Array.isArray(stopTemplatesRef.data) ? stopTemplatesRef.data : [];
    if (templates.length === 0) return undefined;
    const coordsById = new Map<string, StopCoord>();
    for (const [id, s] of stopIndex) {
      coordsById.set(id, { name: s.stop_name, latitude: s.location.latitude, longitude: s.location.longitude });
    }
    return matchNearestSegment(position.location.latitude, position.location.longitude, templates, coordsById);
  }, [movingUntracked, position, stopIndex, stopTemplatesRef.data]);

  // The trip instance's merged live timeline. Unlike the static stop times
  // above, this knows which stops were actually reached and where the bus
  // heads next.
  const [live, setLive] = useState<TripInstanceLiveProgress | null>(null);
  useEffect(() => {
    if (!trip?.id) { setLive(null); return; }
    let cancelled = false;
    const load = () => api.getTripLiveProgress(trip.id)
      .then((lp) => { if (!cancelled) setLive(lp); })
      .catch(() => { if (!cancelled) setLive(null); });
    load();
    const id = window.setInterval(load, 5000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [trip?.id]);

  const timeline = useMemo(() => {
    if (live && live.stops.length > 0) {
      const hhmm = (iso?: string) =>
        iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }) : "";
      return live.stops
        .map((s) => ({
          seq: s.stop_sequence,
          name: s.stop_name || s.stop_id.slice(0, 8),
          arrival: hhmm(s.predicted_arrival ?? s.scheduled_arrival),
          departure: hhmm(s.scheduled_departure),
          actualArr: s.check_in_time,
          actualDep: s.check_out_time,
        }))
        .sort((a, b) => a.seq - b.seq);
    }
    return stopTimes
      .map((st) => {
        const stop = stopIndex.get(st.stop_id);
        return {
          seq: st.stop_sequence,
          name: st.stop_name || stop?.stop_name || st.stop_id.slice(0, 8),
          arrival: st.arrival_time?.slice(0, 5) ?? "",
          departure: st.departure_time?.slice(0, 5) ?? "",
          actualArr: st.actual_arrival,
          actualDep: st.actual_departure,
        };
      })
      .sort((a, b) => a.seq - b.seq);
  }, [live, stopTimes, stopIndex]);

  const currentSeq = live ? live.current_stop_sequence : trip?.current_stop_sequence ?? 0;
  // A stale fix's speed is history, not a reading.
  const fresh = hasFreshFix(position, now);
  const speed = fresh && position?.speed != null ? `${Math.round(position.speed)} km/h` : "— km/h";

  const nextStop = useMemo(() => {
    if (live && live.stops.length > 0) {
      return describeNextStop(nextLiveStop(live)) ?? "Final stop reached";
    }
    const upcoming = timeline.find(s => s.seq >= currentSeq && !s.actualArr);
    const known = upcoming?.name ?? timeline.find(s => s.seq > currentSeq)?.name;
    if (known) return known;
    // No trip at all to read a next stop from — for a bus that's plainly
    // moving, fall back to matching its raw fix against the route network
    // instead of leaving this blank.
    return describeNearestSegment(nearestSegment) ?? "—";
  }, [live, timeline, currentSeq, nearestSegment]);

  const delayText = state === "lost_contact" ? "no signal" : fmtDelayShort(trip?.delay_seconds);
  const tone = state === "lost_contact" ? "crit" : delayTone(trip?.delay_seconds);
  // The marker's look — a panic outranks the state, here as on the map.
  const look = markerLookMeta(panic ? "panic" : state);
  const routeColor = trip?.route_color ? `#${trip.route_color}` : look.color;
  const routeTextColor = trip?.route_text_color ? `#${trip.route_text_color}` : "#fff";
  const chip = rowChip(row);

  return (
    <div className="vdp">
      {/* One row for icon, identity, state and close — the close button used
          to float over the header and cover the end of the state badge
          ("LOST CONTAC×"). */}
      <div className="vdp-header">
        <div className={"vdp-bus-icon" + (panic ? " pulse" : "")} style={{ background: panic ? look.color : routeColor }}>
          <Glyph path={look.glyph} size={20} />
        </div>
        <div className="vdp-header-info">
          <span className="vdp-plate mono">
            {rowLabel(row) || "—"}
            {chip ? <span className="vdp-chip">{chip}</span> : null}
          </span>
          <span className="vdp-driver">{driver || "—"}</span>
        </div>
        <span
          className={"vdp-state-badge" + (look.pulse ? " pulse" : "")}
          style={{ background: look.color }}
        >
          {look.label}
        </span>
        <button className="vdp-close" onClick={onClose} type="button" aria-label="Close vehicle details">&times;</button>
      </div>
      {panic ? (
        <p className="vdp-explain panic" style={{ borderLeftColor: look.color }}>
          Panic alarm active — acknowledge or resolve it under Incidents.
        </p>
      ) : null}

      {trip ? (
        <div className="vdp-route-row">
          <span className="vdp-route-badge" style={{ background: routeColor, color: routeTextColor }}>
            {trip.route_long_name || trip.route_short_name || "—"}
          </span>
          {trip.headsign ? <span className="vdp-trip-id">{trip.headsign}</span> : null}
          <span className={`tag sev-${tone === "ok" ? "ok" : tone === "warn" ? "warn" : tone === "crit" ? "crit" : "info"}`}>
            {delayText.toUpperCase()}
          </span>
        </div>
      ) : null}
      {/* Without a trip there is no route row; say what the bus is doing
          instead — the one line that explains the badge. */}
      {state !== "on_trip" ? (
        <p className="vdp-explain" style={{ borderLeftColor: VEHICLE_LIVE_STATE_META[state].color }}>
          {stateExplanation(row, now)}
        </p>
      ) : null}

      <div className="vdp-stats cols-2">
        <div className="vdp-stat">
          <span className="vdp-stat-label">SPEED</span>
          <span className="vdp-stat-value mono">{speed}</span>
        </div>
        <div className="vdp-stat">
          <span className="vdp-stat-label">NEXT STOP</span>
          <span className="vdp-stat-value">{nextStop}</span>
        </div>
      </div>

      {timeline.length > 0 && (
        <div className="vdp-timeline">
          <span className="vdp-timeline-label">STATION TIMELINE</span>
          <div className="vdp-timeline-list">
            {timeline.map((s) => {
              const visited = s.seq < currentSeq;
              const current = s.seq === currentSeq;
              const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
              // Still at the current stop: the real arrival is the only honest
              // time — its scheduled departure may be hours stale.
              const atStopNow = current && s.actualArr && !s.actualDep;
              const prefix = atStopNow ? "arr" : visited || current ? "dep" : "eta";
              const time = atStopNow
                ? clock(s.actualArr!)
                : visited || current
                  ? (s.actualDep ? clock(s.actualDep) : s.departure)
                  : s.arrival;
              return (
                <div key={s.seq} className={`vdp-stop ${visited ? "visited" : ""} ${current ? "current" : ""}`}>
                  <span className="vdp-stop-dot" />
                  <span className={`vdp-stop-name${current ? " bold" : ""}`}>{s.name}</span>
                  <span className="vdp-stop-time mono">{prefix} {time}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Sidebar ────────────────────────────────────────────────────────────

export default function MapSidebar({
  rows,
  selectedRow,
  liveKnown,
  rosterLoaded = true,
  liveCount,
  routeFilter,
  routeCount,
  totalCount,
  onClearRouteFilter,
  query,
  onQueryChange,
  alerts,
  acks,
  onAck,
  selectedVehicleId,
  onSelectVehicle,
  panicVehicleIds,
  onResolve,
}: MapSidebarProps) {
  const now = useNowTick(1000);
  const [tab, setTab] = useState<"vehicles" | "incidents">("vehicles");

  const activeAlerts = alerts.filter(a => a.is_active);
  const listed = liveKnown ? rows : [];
  const panicking = (r: LiveVehicleRow) => !!r.vehicleId && !!panicVehicleIds?.has(r.vehicleId);

  return (
    <aside className="map-sidebar">
      {/* Vehicle detail panel — shows when a vehicle is selected */}
      {selectedRow && liveKnown && (
        <VehicleDetailPanel
          row={selectedRow}
          panic={panicking(selectedRow)}
          now={now}
          onClose={() => onSelectVehicle(undefined)}
        />
      )}

      {/* Sidebar header — reflects whichever tab is active */}
      <div className="ms-head">
        <h2>{tab === "incidents" ? "INCIDENTS" : "VEHICLES"}</h2>
        {tab === "incidents" ? (
          <span className={"ms-tracked mono" + (activeAlerts.length > 0 ? " warn" : "")}>
            {activeAlerts.length} ACTIVE
          </span>
        ) : (
          // Buses with a live fix. This counted any fix up to six hours old,
          // and before that the whole roster: "35 TRACKED" with eight reporting.
          <span className="ms-tracked mono" title="Buses with a GPS fix in the last 90 seconds">
            {liveKnown ? liveCount : "—"} LIVE
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="ms-tabs">
        <button className={tab === "vehicles" ? "on" : ""} onClick={() => setTab("vehicles")} type="button">
          VEHICLES <span className="ms-tab-ct">{liveKnown ? rows.length : "—"}</span>
        </button>
        <button className={tab === "incidents" ? "on" : ""} onClick={() => setTab("incidents")} type="button">
          INCIDENTS <span className={"ms-tab-ct" + (activeAlerts.length > 0 ? " warn" : "")}>{activeAlerts.length}</span>
        </button>
      </div>

      {/* Tab content */}
      <div className="ms-body">
        {tab === "vehicles" && (
          <>
            {routeFilter && (
              // The map and this list are both narrowed to one route; say so
              // where the missing buses would have been.
              <div className="ms-route-filter" role="status">
                <span className="mso-route-dot" style={{ background: routeFilter.color ?? "var(--text-4)" }} />
                <span className="ms-route-filter-text">
                  {routeFilter.label} only · {routeCount} of {totalCount}
                </span>
                <button type="button" onClick={onClearRouteFilter}>Show all</button>
              </div>
            )}
            <div className="ms-filter">
              <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Filter by plate, driver or route"
                aria-label="Filter vehicles"
                value={query}
                onChange={e => onQueryChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && listed[0]) onSelectVehicle(listed[0].key);
                  else if (e.key === "Escape") onQueryChange("");
                }}
              />
            </div>
            <div className="ms-vehicle-list">
              {listed.map(v => {
                const isSelected = v.key === selectedVehicleId;
                const panic = panicking(v);
                // The marker's look, so the row and the map agree at a glance.
                const look = markerLookMeta(panic ? "panic" : v.state);
                const onTripLike = v.state === "on_trip" || v.state === "lost_contact";
                const badge = onTripLike ? routeBadgeLabel(v.trip) : "";
                const fresh = hasFreshFix(v.position, now);
                const speed = fresh && v.position?.speed != null ? `${Math.round(v.position.speed)} km/h` : "";
                const chip = rowChip(v);

                return (
                  <div
                    key={v.key}
                    className={`ms-vcard${isSelected ? " sel" : ""}`}
                    onClick={() => onSelectVehicle(v.key)}
                  >
                    <div className={"ms-vcard-icon" + (look.pulse ? " pulse" : "")} style={{ background: look.color }}>
                      <Glyph path={look.glyph} size={18} />
                    </div>
                    <div className="ms-vcard-info">
                      <div className="ms-vcard-top">
                        <span className="ms-vcard-plate mono">{rowLabel(v) || "—"}</span>
                        {badge && (
                          <span className="ms-vcard-badge" style={{ background: routeBadgeColor(v.trip), color: "#fff" }}>{badge}</span>
                        )}
                        {panic && <span className="ms-vcard-chip crit">Panic</span>}
                        {chip && <span className="ms-vcard-chip">{chip}</span>}
                      </div>
                      <span className="ms-vcard-sub" title={rowSubtitle(v, now)}>{rowSubtitle(v, now)}</span>
                    </div>
                    <div className="ms-vcard-right">
                      {v.state === "lost_contact" ? (
                        // A connectivity fact, not a delay reading.
                        <span className="ms-vcard-delay ms-delay--crit">no signal</span>
                      ) : v.state === "on_trip" && v.trip ? (
                        // Only with a real trip row behind it: v.state can say
                        // "on_trip" a beat before the separately-polled trips
                        // list catches up, and fmtDelayShort(undefined) reads
                        // as "on time" — a false all-clear, not a missing value.
                        <span className={`ms-vcard-delay ms-delay--${delayTone(v.trip.delay_seconds)}`}>{fmtDelayShort(v.trip.delay_seconds)}</span>
                      ) : speed ? (
                        // No trip to measure against: the live speed is the
                        // reading that matters.
                        <span className="ms-vcard-delay">{speed}</span>
                      ) : null}
                      {onTripLike && speed && <span className="ms-vcard-speed mono">{speed}</span>}
                    </div>
                  </div>
                );
              })}
              {listed.length === 0 && (
                <div className="ms-empty">
                  {!rosterLoaded
                    ? "Loading vehicles…"
                    : !liveKnown
                      ? "Waiting for live data…"
                      : query.trim()
                        ? `No bus matches “${query.trim()}”${routeFilter ? ` on ${routeFilter.label}` : ""}.`
                        : routeFilter
                          ? `No bus is running ${routeFilter.label} right now.`
                          : "No vehicles found."}
                </div>
              )}
            </div>
          </>
        )}

        {tab === "incidents" && (
          <div className="ms-incidents">
            {activeAlerts.length === 0 && (
              <div className="ms-empty">No active incidents.</div>
            )}
            {activeAlerts.map(alert => {
              const severity = severityToUi(alert.severity);
              const ackInfo = acks[alert.id];
              const since = now - new Date(alert.active_period_start ?? alert.created_at).getTime();
              const dotCls = severity === "crit" ? "sev-crit" : severity === "warn" ? "sev-warn" : "sev-info";
              return (
                <div key={alert.id} className={`ms-incident sev-${severity}`}>
                  <div className="ms-incident-head">
                    <span className={`dot ${dotCls}`} />
                    <span className="ms-incident-title">{alert.header_text}</span>
                    <span className="ms-incident-ago mono">{fmtAgo(since)}</span>
                  </div>
                  {alert.description_text && (
                    <p className="ms-incident-desc">{alert.description_text}</p>
                  )}
                  <div className="ms-incident-actions">
                    {!ackInfo ? (
                      <button className="btn primary" type="button" onClick={() => onAck(alert.id)}>Acknowledge</button>
                    ) : (
                      <button className="btn primary" type="button" onClick={() => void onResolve(alert.id)}>Resolve</button>
                    )}
                    {alert.metadata?.vehicle_id && (
                      <button className="btn ghost" type="button" onClick={() => onSelectVehicle(alert.metadata.vehicle_id!)}>Locate</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
